/**
 * Utility type to convert literal types to their base types
 */
type InferBaseType<D> = D extends number
  ? number
  : D extends string
    ? string
    : D extends boolean
      ? boolean
      : D extends Date
        ? Date
        : D extends Map<infer K, infer V>
          ? Map<K, V>
          : D extends Set<infer T>
            ? Set<T>
            : D extends Array<infer T>
              ? T[]
              : D extends Record<any, any>
                ? D
                : D

/**
 * Get a value safely from an object or class instance with type inference.
 * it can access nested keys up to 3 levels deep.
 *
 * @example
 * // For objects:
 * - getSafely(state[tableId], ['rows'], [] as IRow[]) // returns IRow[]
 *
 * // For class instances:
 * - getSafely(table, ['settings', '_id'], '') // returns string
 *
 * // For primitive types:
 * - getSafely(table, ['settings', '_Language', '_IsHidden'], false) // returns boolean
 *
 */
export function getSafely<
  T extends object | undefined | null,
  K1 extends T extends object ? keyof T : never,
  K2 extends T extends object ? (T[K1] extends object ? keyof T[K1] : never) : never,
  K3 extends T extends object
    ? T[K1] extends object
      ? T[K1][K2] extends object
        ? keyof T[K1][K2]
        : never
      : never
    : never,
  D,
>(obj: T, keys: [K1] | [K1, K2] | [K1, K2, K3], defaultValue: D): InferBaseType<D> {
  if (!obj) return defaultValue as InferBaseType<D>

  return keys.reduce(
    (xs, x) => (xs && xs[x as keyof typeof xs] !== undefined ? xs[x as keyof typeof xs] : defaultValue),
    obj as any
  )
}
